/**
 * Ошибки сервера человеческим языком.
 *
 * Сервер отдаёт код, а не текст: язык знает клиент. Незнакомый код
 * показываем как есть — это честнее, чем универсальное «что-то пошло не
 * так», по которому нельзя ни понять причину, ни написать в поддержку.
 *
 * Список кодов повторяет серверный. Проверка «есть ли строка» здесь, а не
 * попытка перевести вслепую: иначе на экране оказался бы сам ключ
 * `err.что_то`, что хуже английского текста.
 */

import { useTranslation } from 'react-i18next';
import {
  BadRequestError,
  TonConnectError,
  UnknownAppError,
  UnknownError,
  UserRejectsError,
  WalletNotConnectedError,
  WalletWrongNetworkError,
} from '@tonconnect/ui-react';
import { MIN_PAYMENT } from '@78/core';
import { CURRENCY, formatMoney, type Lang } from '@78/i18n';

/** Коды, для которых в каталоге есть строка `err.<код>`. */
const KNOWN = new Set([
  'price_zero',
  'bad_tier',
  'too_many_deferrals',
  'bad_payments_count',
  'period_too_short',
  'upfront_too_small',
  'upfront_too_large',
  'upfront_out_of_range',
  'upfront_below_min',
  'payment_below_min',
  'defer_fee_too_high',
  'deadline_too_far',
  'schedule_past_deadline',
  'request_not_live',
  'counter_not_live',
  'not_your_request',
  'not_your_counter',
  'seller_is_buyer',
  'rounds_exhausted',
  // Отказы именного оффера: адресат у него один, и вещь в нём одна.
  'not_the_owner',
  'other_item',
  'bad_terms',
  'deal_not_found',
  'request_not_found',
  'counter_not_found',
  'bad_address',
  'no_such_method',
  'no_init_data',
  'no_address',
  'no_collection',
  'wallet_not_confirmed',
  'proof_required',
  'bad_proof_payload',
  'unknown',
  'network',
  'connect_wallet',
  'nothing_to_pay',
  'internal',
  'bad_body',
  'api_disabled',
  // Приглашения: их причины доходят до экрана «Приглашайте и зарабатывайте»
  // мелким шрифтом под общим объяснением. Без перевода человек видел бы там
  // сырое `referrals_off` — это и есть тот случай, ради которого каталог
  // вообще существует.
  'referrals_off',
  'unknown_code',
  'self_wallet',
  'self_account',
  'forbidden',
  // Причины от auth.ts: сюда попадают при проблемах с initData и ton_proof.
  'no_signature',
  'signature_not_hex',
  'signature_mismatch',
  'no_auth_date',
  'init_data_expired',
  'auth_date_future',
  'no_user',
  'no_user_id',
  'bad_user',
  'domain_not_allowed',
  'proof_expired',
  'proof_future',
  'payload_mismatch',
  'key_not_hex',
  'bad_key',
  'key_not_32_bytes',
  'key_not_owner',
  'signature_not_base64',
  'wallet_unreachable',
]);

/**
 * Провал доставки запроса до кошелька.
 *
 * TON Connect несёт запрос до кошелька через свой мост. Любая ошибка,
 * которая не является его собственной (оборванный fetch, недоступный мост),
 * приезжает к нам обёрнутой в «Unhandled error: …» — и ровно это человек и
 * видел на экране вместо объяснения. Показывать ему внутреннее сообщение
 * библиотеки бессмысленно: оно не говорит ни что случилось, ни что делать,
 * ни того главного, что деньги и гифт остались на месте.
 */
const WALLET_UNREACHABLE =
  /unhandled error|failed to fetch|networkerror|load failed|network request failed/i;

/**
 * Читаемое имя ошибки TON Connect.
 *
 * В журнал попадало минифицированное `ji`: по нему нельзя отличить отказ
 * кошелька от испорченного запроса. `instanceof` переживает минификацию,
 * имя класса — нет, поэтому имя собирается здесь руками.
 */
export function tonConnectKind(e: unknown): string | null {
  if (e instanceof UserRejectsError) return 'UserRejectsError';
  if (e instanceof WalletNotConnectedError) return 'WalletNotConnectedError';
  if (e instanceof WalletWrongNetworkError) return 'WalletWrongNetworkError';
  if (e instanceof BadRequestError) return 'BadRequestError';
  if (e instanceof UnknownAppError) return 'UnknownAppError';
  if (e instanceof UnknownError) return 'UnknownError (кошелёк отказал без кода)';
  if (e instanceof TonConnectError) return 'TonConnectError';
  return null;
}

export function useErrorText(): (e: unknown) => string {
  const { t, i18n } = useTranslation();
  return (e: unknown) => {
    const raw = e instanceof Error ? e.message : String(e ?? '');
    // Минимальный платёж подставляется суммой: коды приходят и от сервера,
    // а он про валюту и язык человека ничего не знает.
    const lang = (i18n.language === 'ru' ? 'ru' : 'en') as Lang;
    if (KNOWN.has(raw)) return t(`err.${raw}`, { min: `${formatMoney(MIN_PAYMENT, lang)} ${CURRENCY}` });
    // Отказ пользователя в кошельке — не ошибка сервиса и не повод пугать.
    // Сюда же закрытое окно подтверждения: «Transaction was not sent» — это
    // человек передумал, а не сбой.
    if (/reject|cancell?ed|user declined|was not sent/i.test(raw)) return '';
    if (WALLET_UNREACHABLE.test(raw)) return t('err.wallet_unreachable');

    /**
     * Ошибки TON Connect — по типу, а не по тексту.
     *
     * Текст у них собирается как «[TON_CONNECT_SDK_ERROR] <имя класса>», а
     * имя класса минифицируется при сборке: человек видел на экране
     * `[TON_CONNECT_SDK_ERROR] ji` и не мог ни понять причину, ни
     * пересказать её в поддержку. `instanceof` переживает минификацию, а
     * текст — нет.
     */
    if (e instanceof WalletNotConnectedError) return t('err.wallet_disconnected');
    if (e instanceof WalletWrongNetworkError) return t('err.wallet_wrong_network');
    if (e instanceof UserRejectsError) return '';
    if (e instanceof TonConnectError) return t('err.wallet_sdk');
    if (raw.includes('[TON_CONNECT_SDK_ERROR]')) return t('err.wallet_sdk');

    if (raw.length === 0) return t('err.unknown');
    return raw;
  };
}
